import { useState } from "react";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";

const testimonials = [
    {
        name: "Sarah Chen",
        role: "Founder, Halcyon Capital",
        avatar: "https://i.pravatar.cc/128?img=32",
        text: "Aximo did the rare thing — they argued with us, then made us look like geniuses. Site traffic doubled in eight weeks and the pipeline followed.",
    },
    {
        name: "Marcus Reyes",
        role: "CMO, Northwind AM",
        avatar: "https://i.pravatar.cc/128?img=12",
        text: "We shipped 140 campaign pages in a year with zero compliance escalations. The team turned a bottleneck into a compounding asset.",
    },
    {
        name: "Ava Nakamura",
        role: "CEO, Maison Verre",
        avatar: "https://i.pravatar.cc/128?img=68",
        text: "Every deliverable arrived with instrumentation baked in. We stopped guessing at what worked; the dashboard told us on day one.",
    },
    {
        name: "David Okafor",
        role: "VP Growth, Atlas Commerce",
        avatar: "https://i.pravatar.cc/128?img=47",
        text: "Best three-quarters of ad spend we've ever run. Our CPA dropped 42% and we finally stopped hiring freelancers to babysit a Meta account.",
    },
];

export default function DigtekTestimonials() {
    const [i, setI] = useState(0);
    const prev = () => setI((v) => (v - 1 + testimonials.length) % testimonials.length);
    const next = () => setI((v) => (v + 1) % testimonials.length);
    const t = testimonials[i];

    return (
        <section
            data-testid="digtek-testimonials"
            className="relative py-24 lg:py-32 bg-digtek-bg overflow-hidden"
        >
            <div className="digtek-blob lime w-[340px] h-[340px] left-0 top-24 opacity-40" />
            <div className="digtek-blob purple w-[380px] h-[380px] right-0 bottom-12 opacity-40" />

            <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
                <div className="text-center mb-14">
                    <div className="digtek-eyebrow mb-4 justify-center">
                        <span>Testimonials</span>
                    </div>
                    <h2
                        data-testid="testimonials-heading"
                        className="font-display font-bold text-[40px] sm:text-[52px] lg:text-[62px] leading-[1.02] text-digtek-header max-w-[820px] mx-auto"
                    >
                        What our awesome{" "}
                        <em className="not-italic text-digtek-purple">
                            customers say
                        </em>
                    </h2>
                    <p className="mt-6 text-digtek-text text-lg max-w-[620px] mx-auto">
                        No paid testimonials. Just receipts from founders and
                        marketers who let us handle the growth loop.
                    </p>
                </div>

                <div className="max-w-[880px] mx-auto">
                    <div
                        key={i}
                        data-testid={`testimonial-card-${i}`}
                        className="digtek-testimonial-card animate-fade-up"
                    >
                        <div className="flex items-start justify-between gap-6 mb-6">
                            <div className="flex items-center gap-4">
                                <img
                                    src={t.avatar}
                                    alt={t.name}
                                    className="w-16 h-16 rounded-full object-cover ring-4 ring-digtek-bg"
                                />
                                <div>
                                    <p className="font-display font-bold text-xl text-digtek-header">
                                        {t.name}
                                    </p>
                                    <p className="text-sm text-digtek-purple font-semibold">
                                        {t.role}
                                    </p>
                                </div>
                            </div>
                            <div className="w-14 h-14 rounded-full bg-digtek-lime flex items-center justify-center shrink-0">
                                <Quote className="w-6 h-6 text-digtek-header" />
                            </div>
                        </div>
                        <p className="text-digtek-text text-lg leading-[1.7]">
                            "{t.text}"
                        </p>
                        <div className="mt-6 flex items-center gap-1">
                            {["s1", "s2", "s3", "s4", "s5"].map((sk) => (
                                <Star
                                    key={sk}
                                    className="w-5 h-5 fill-current"
                                    style={{ color: "#FFB800" }}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 flex items-center justify-center gap-4">
                        <button
                            onClick={prev}
                            data-testid="testimonial-prev"
                            aria-label="Previous"
                            className="w-14 h-14 rounded-full border-2 border-digtek-header text-digtek-header hover:bg-digtek-header hover:text-digtek-lime transition-colors flex items-center justify-center"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-2">
                            {testimonials.map((tItem, j) => (
                                <button
                                    key={tItem.name}
                                    onClick={() => setI(j)}
                                    aria-label={`Go to testimonial ${j + 1}`}
                                    data-testid={`testimonial-dot-${j}`}
                                    className={`h-2 rounded-full transition-all ${
                                        j === i
                                            ? "w-8 bg-digtek-purple"
                                            : "w-2 bg-digtek-purple/30"
                                    }`}
                                />
                            ))}
                        </div>
                        <button
                            onClick={next}
                            data-testid="testimonial-next"
                            aria-label="Next"
                            className="w-14 h-14 rounded-full border-2 border-digtek-header text-digtek-header hover:bg-digtek-header hover:text-digtek-lime transition-colors flex items-center justify-center"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
